import { Injectable } from '@angular/core';
import { DateItem as BaseDateItem } from './fitness-plan-persistence.service';

export interface TemplateDay {
  dayOfWeek: number;
  dayName: string;
  isRestDay: boolean;
  exercises: any[];
}

export interface FitnessTemplate {
  id: string;
  name: string;
  days: TemplateDay[];
}

// Extended DateItem interface for template management (compatible with component)
export interface DateItem {
  date: Date;
  formattedDate: string;
  dayName: string;
  isExpanded: boolean;
  exercises: any[];
  isRestDay: boolean;
  restDayAllotmentId?: string;
}

@Injectable({
  providedIn: 'root',
})
export class TemplateManagementService {
  constructor() {}

  /**
   * Apply a fitness template to a range of dates
   * @param template - The template to apply
   * @param dateList - Array of date items to apply template to
   * @param pendingDeletions - Set to track pending deletions
   * @param exerciseResolver - Function to resolve exercise names from IDs
   * @returns Updated date list with template applied
   */
  applyTemplateToDateRange(
    template: FitnessTemplate,
    dateList: DateItem[],
    pendingDeletions: Set<string>,
    exerciseResolver: (id: any) => string
  ): DateItem[] {
    if (!template || !dateList.length) {
      console.log('No template selected or no date list available');
      return dateList;
    }

    console.log('Applying template to date range:', template);

    const templateDays = template.days || [];
    const today = this.getTodayAtMidnight();

    dateList.forEach((dateItem, index) => {
      // Skip template application for current or past dates
      const itemDate = new Date(dateItem.date);
      itemDate.setHours(0, 0, 0, 0);
      if (itemDate <= today) {
        console.log(
          `Skipping template application for ${dateItem.formattedDate} - current or past date`
        );
        return;
      }

      this.markExistingExercisesForDeletion(
        dateItem,
        pendingDeletions,
        templateDays
      );
      this.applyTemplateDayToDateItem(dateItem, templateDays, exerciseResolver);
    });

    return dateList;
  }

  /**
   * Mark existing exercises and rest day allotments for deletion when applying template
   * @param dateItem - The date item to process
   * @param pendingDeletions - Set to track pending deletions
   * @param templateDays - Array of template days to determine what will be applied
   */
  private markExistingExercisesForDeletion(
    dateItem: DateItem,
    pendingDeletions: Set<string>,
    templateDays: TemplateDay[]
  ): void {
    // Only mark exercises with allotmentId for deletion (exercises that exist in the database)
    // Manually added exercises (without allotmentId) should be removed directly without marking for deletion
    dateItem.exercises.forEach((exercise) => {
      if (exercise.allotmentId) {
        // This exercise exists in the database - mark for deletion
        exercise.isMarkedForDeletion = true;
        pendingDeletions.add(exercise.allotmentId);
        console.log(
          `Marked exercise for deletion: ${exercise.exercise} (ID: ${exercise.allotmentId})`
        );
      }
    });

    // Remove manually added exercises (without allotmentId) directly
    dateItem.exercises = dateItem.exercises.filter(
      (exercise) => exercise.allotmentId || exercise.isMarkedForDeletion
    );

    console.log(
      `After filtering: ${dateItem.exercises.length} exercises remaining for ${dateItem.formattedDate}`
    );

    // Only mark rest day allotment for deletion if the template will change it to a workout day
    if (dateItem.isRestDay && dateItem.restDayAllotmentId) {
      const templateDay = this.findMatchingTemplateDay(dateItem, templateDays);

      // If template day is not a rest day, then we need to delete the existing rest day
      if (templateDay && !templateDay.isRestDay) {
        pendingDeletions.add(dateItem.restDayAllotmentId);
        console.log(
          `Marked rest day allotment for deletion (converting to workout): ${dateItem.restDayAllotmentId} for ${dateItem.formattedDate}`
        );
      } else {
        console.log(
          `Keeping existing rest day allotment (template is also rest day): ${dateItem.restDayAllotmentId} for ${dateItem.formattedDate}`
        );
      }
    }
  }

  /**
   * Apply a specific template day to a date item
   * @param dateItem - The date item to modify
   * @param templateDays - Array of template days
   * @param exerciseResolver - Function to resolve exercise names from IDs
   */
  private applyTemplateDayToDateItem(
    dateItem: DateItem,
    templateDays: TemplateDay[],
    exerciseResolver: (id: any) => string
  ): void {
    const templateDay = this.findMatchingTemplateDay(dateItem, templateDays);

    if (!templateDay) {
      return;
    }

    console.log(
      `Found matching template day for ${dateItem.dayName}:`,
      templateDay
    );

    if (templateDay.isRestDay) {
      dateItem.isRestDay = true;
      // Only clear rest day allotment ID if it wasn't preserved (i.e., if this is a new rest day)
      // If the existing day was already a rest day and wasn't marked for deletion, keep the allotment ID
      if (!dateItem.restDayAllotmentId) {
        console.log(
          `Applying new rest day template to ${dateItem.formattedDate}`
        );
      } else {
        console.log(
          `Preserving existing rest day for ${dateItem.formattedDate}`
        );
      }
      return;
    }

    this.applyTemplateExercises(
      dateItem,
      templateDay.exercises,
      exerciseResolver
    );
  }

  /**
   * Find the template day that matches the given date item
   * @param dateItem - The date item to match
   * @param templateDays - Array of template days
   * @returns Matching template day or null
   */
  private findMatchingTemplateDay(
    dateItem: DateItem,
    templateDays: TemplateDay[]
  ): TemplateDay | null {
    const jsDay = dateItem.date.getDay();
    // Template uses: 1=Sunday, 2=Monday, 3=Tuesday, 4=Wednesday, 5=Thursday, 6=Friday, 7=Saturday
    const templateDay = jsDay === 0 ? 1 : jsDay + 1;

    return (
      templateDays.find((day: any) => {
        const matchesDayOfWeek = day.dayOfWeek === templateDay;
        const matchesDayName =
          day.dayName?.toLowerCase() === dateItem.dayName.toLowerCase();
        return matchesDayOfWeek || matchesDayName;
      }) || null
    );
  }

  /**
   * Apply template exercises to a date item
   * @param dateItem - The date item to modify
   * @param templateExercises - Array of exercises from template
   * @param exerciseResolver - Function to resolve exercise names from IDs
   */
  private applyTemplateExercises(
    dateItem: DateItem,
    templateExercises: any[],
    exerciseResolver: (id: any) => string
  ): void {
    if (!templateExercises || templateExercises.length === 0) {
      return;
    }

    dateItem.isRestDay = false;
    // Clear rest day allotment ID when converting to workout day
    dateItem.restDayAllotmentId = undefined;

    let exerciseId = Math.max(0, ...dateItem.exercises.map((ex) => ex.id)) + 1;

    templateExercises.forEach((templateEx: any) => {
      const exerciseName = exerciseResolver(templateEx.exerciseId);
      const section = templateEx.section || 'main';

      this.replaceOrAddExercise(
        dateItem,
        templateEx,
        exerciseName,
        section,
        exerciseId
      );
      exerciseId++;
    });
  }

  /**
   * Replace existing marked exercise or add new exercise from template
   * @param dateItem - The date item to modify
   * @param templateEx - Template exercise data
   * @param exerciseName - Resolved exercise name
   * @param section - Exercise section
   * @param exerciseId - New exercise ID
   */
  private replaceOrAddExercise(
    dateItem: DateItem,
    templateEx: any,
    exerciseName: string,
    section: string,
    exerciseId: number
  ): void {
    // Check if there's an existing exercise with the same name and section that's marked for deletion
    const existingMarkedExercise = dateItem.exercises.find(
      (ex) =>
        ex.isMarkedForDeletion &&
        ex.exercise === exerciseName &&
        ex.section === section
    );

    if (existingMarkedExercise) {
      // Replace the marked exercise instead of adding a new one
      const existingIndex = dateItem.exercises.indexOf(existingMarkedExercise);
      if (existingIndex !== -1) {
        dateItem.exercises.splice(existingIndex, 1);
      }
    }

    // Create new exercise from template
    const newExercise = this.createExerciseFromTemplate(
      templateEx,
      exerciseName,
      section,
      exerciseId
    );
    dateItem.exercises.push(newExercise);
  }

  /**
   * Create an exercise object from template data
   * @param templateEx - Template exercise data
   * @param exerciseName - Resolved exercise name
   * @param section - Exercise section
   * @param exerciseId - Exercise ID
   * @returns New exercise object
   */
  private createExerciseFromTemplate(
    templateEx: any,
    exerciseName: string,
    section: string,
    exerciseId: number
  ): any {
    let sets = [];

    if (templateEx.sets && Array.isArray(templateEx.sets)) {
      sets = templateEx.sets.map((set: any, setIndex: number) => {
        const timeData = this.processTemplateSetTime(set.time);

        return {
          setNumber: setIndex + 1,
          reps: set.reps || '',
          time: timeData.timeString,
          timeMinutes: timeData.timeMinutes,
          timeSeconds: timeData.timeSeconds,
          weights: set.weights || '',
          status: 'new',
        };
      });
    } else {
      // Create a default set if none provided
      sets = [
        {
          setNumber: 1,
          reps: '',
          time: '',
          timeMinutes: 0,
          timeSeconds: 0,
          weights: '',
          status: 'new',
        },
      ];
    }

    return {
      id: exerciseId,
      exercise: exerciseName,
      exerciseId: templateEx.exerciseId, // Add the template's exerciseId for restore functionality
      sets: sets,
      section: section,
      isMarkedForDeletion: false,
    };
  }

  /**
   * Process time data from template sets
   * @param timeValue - Time value from template (number or string)
   * @returns Processed time data
   */
  private processTemplateSetTime(timeValue: any): {
    timeString: string;
    timeMinutes: number;
    timeSeconds: number;
  } {
    let timeString = '';
    let timeMinutes = 0;
    let timeSeconds = 0;

    if (timeValue) {
      if (typeof timeValue === 'number') {
        // If time is a number, assume it's total seconds
        const totalSeconds = timeValue;
        timeMinutes = Math.floor(totalSeconds / 60);
        timeSeconds = totalSeconds % 60;
        timeString = `${timeMinutes.toString().padStart(2, '0')}:${timeSeconds
          .toString()
          .padStart(2, '0')}`;
      } else if (typeof timeValue === 'string') {
        // If time is already a string, parse it
        const timeMatch = timeValue.match(/^(\d{1,2}):(\d{2})$/);
        if (timeMatch) {
          timeMinutes = parseInt(timeMatch[1], 10);
          timeSeconds = parseInt(timeMatch[2], 10);
          timeString = timeValue;
        } else {
          // Try to parse as total seconds if it's a numeric string
          const numericValue = parseInt(timeValue, 10);
          if (!isNaN(numericValue)) {
            timeMinutes = Math.floor(numericValue / 60);
            timeSeconds = numericValue % 60;
            timeString = `${timeMinutes
              .toString()
              .padStart(2, '0')}:${timeSeconds.toString().padStart(2, '0')}`;
          }
        }
      }
    }

    return { timeString, timeMinutes, timeSeconds };
  }

  /**
   * Get today's date at midnight for comparison
   * @returns Date object set to today at midnight
   */
  private getTodayAtMidnight(): Date {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  }

  /**
   * Calculate which template day corresponds to a JavaScript day
   * @param jsDay - JavaScript day (0-6, Sunday-Saturday)
   * @returns Template day number (1-7, Sunday-Saturday)
   */
  mapJSDayToTemplateDay(jsDay: number): number {
    return jsDay === 0 ? 1 : jsDay + 1;
  }

  /**
   * Get the highest exercise ID from a date item
   * @param dateItem - Date item to examine
   * @returns Highest exercise ID
   */
  getHighestExerciseId(dateItem: DateItem): number {
    if (!dateItem.exercises || dateItem.exercises.length === 0) {
      return 0;
    }
    return Math.max(...dateItem.exercises.map((ex) => ex.id || 0));
  }
}
